import m from "mithril";
import {Highlighter, Area, Category, Style} from "../../model/items/imageItem";
import {Vnode} from "mithril";
import Point from "./ImageHighlighter/Point";
import PointComment from "./ImageHighlighter/PointComment";

export default class ImageHighlighter {

    public static readonly POINT_RADIUS = 20;

    public static component = {
        view: (vnode :Vnode) => {
            return m(ImageHighlighter, vnode.attrs);
        }
    };

    public highlighter!: Highlighter;
    private selectedCategory!: Category;
    private element?: Element;
    private canvas?: HTMLCanvasElement;
    private content?: any;
    private selectedArea?: Area;
    private static dialog: PointComment|null = null;
    private ratio = {h: 1, w: 1};

    public static closeDialog(): void {
        this.dialog = null;
    }

    private get comments(): boolean {
        return this.highlighter.comments;
    }

    private computeRatio(): void {
        this.ratio = {h: 1, w: 1};
        if (this.element) {
            const img = <HTMLImageElement|undefined>$(this.element).find('img.image')[0];
            if (img) {
                this.ratio = {h: img.offsetHeight / img.naturalHeight, w: img.offsetWidth / img.naturalWidth}
            }
        }
    }

    oninit(vnode: Vnode) {
        this.highlighter = (<any>vnode.attrs).highlighter;
        if( this.highlighter.getCategories().length > 0 ) {
            this.selectedCategory = this.highlighter.getCategories()[0];
        } else {
            this.selectedCategory = new Category('', 0, 0, 1, new Style('transparent', 'black'));
            this.selectedCategory.value = 0;
        }
        this.content = (<any>vnode.attrs).content;
        this.content.attrs.onload = () => this.onResize();
    }

    onMouseClick(event: MouseEvent) {
        if (this.comments) {
            let point = this.highlighter.searchPoint(this.selectedCategory.value, this.evtToPoint(event), false);
            if (!point && ImageHighlighter.dialog) {
                ImageHighlighter.closeDialog()
            } else if (!point && this.highlighter.canAddPoint(this.selectedCategory.value)) {
                // Create new point
                point = this.evtToPoint(event);
                this.highlighter.addOrRemovePoint(this.selectedCategory.value, point); // Add point
                ImageHighlighter.dialog = new PointComment(this, point, () => ImageHighlighter.closeDialog()); // open dialog
            } else if (point) {
                // Open dialog on selected point
                ImageHighlighter.dialog = new PointComment(this, point, () => ImageHighlighter.closeDialog()); // open dialog
            }

        } else {
            this.highlighter.addOrRemovePoint(this.selectedCategory.value, this.evtToPoint(event));
        }
    }

    private addOrRemovePoint(event: MouseEvent) {
        this.highlighter.addOrRemovePoint(this.selectedCategory.value, this.evtToPoint(event));
    }


    checkArea(evt: MouseEvent) {
        const point = this.evtToPoint(evt);
        this.selectedArea = undefined;
        this.highlighter.getAreas().forEach(area => {
            if( area.isInside(point) ) {
                this.selectedArea = area;
            }
        })
    }

    clickArea(evt: MouseEvent) {
        if( this.selectedArea ) {
            this.highlighter.onAreaClicked(this.selectedArea, this.selectedCategory);
        }
    }

    view(): Vnode {
        switch(this.highlighter.type) {
            case Highlighter.Type.POINT:
                return this.viewPoint();
            case Highlighter.Type.PICK_AREA:
                return this.viewPickArea();
            default:
                return this.content;
        }
    }

    viewPoint() {
        const size = ImageHighlighter.POINT_RADIUS * 2;
        const radius = ImageHighlighter.POINT_RADIUS;
        return m('.imageHighlighter'+(ImageHighlighter.dialog ? '.open-dialog' : ''), [
            this.viewCategories(),
            m(".imageHighlighterContainer",
                {
                    oncreate: (vnode) => {
                        this.element = vnode.dom;
                    },
                    onclick: (e:MouseEvent) => this.onMouseClick(e)
                },
                [
                    this.content,
                    ImageHighlighter.dialog ? m(ImageHighlighter.dialog) : null
                ].concat(
                    this.highlighter.getCategories().map(
                        cat => this.highlighter.points[cat.value].map(
                            p => m('.point-wrapper', {
                                style: `
                                    left: ${this.pointByElementSize(p.x, p.y).x}px;
                                    top: ${this.pointByElementSize(p.x, p.y).y}px;
                                    width: ${size}px;
                                    height: ${size}px;`
                            },  [
                                m('.point.pop-in',  {
                                    style: `
                                        width: ${size}px;
                                        height: ${size}px;                                       
                                        -webkit-border-radius: ${radius}px;
                                        -moz-border-radius: ${radius}px;
                                        border-radius: ${radius}px;
                                        background-color:${cat.style.fillColor};
                                        border-color: ${cat.style.borderColor}`
                                }, p.comment ? m.trust('<i class="icon icon-message-text text-light"></i>') : '')
                            ])
                        )
                    )
                )
            )
        ]);
    }

    viewPickArea() {
        this.content.onload = () => this.onResize();
        return m('div.imageHighlighter', [
            this.viewCategories(),
            m("div.imageHighlighterContainer", {
                    oncreate: vnode => this.element = vnode.dom,
                    onupdate: () => this.drawCanvas(),
                    onclick: (e: MouseEvent) => this.clickArea(e),
                    onmousemove: (e: MouseEvent) => this.checkArea(e),
                    onmouseout: (e: MouseEvent) => this.selectedArea = undefined,
                },
                [this.content].concat(
                    m('canvas', {
                        width: '100%',
                        height: '100%',
                        style : "position:absolute; top:0px; left:0px;",
                        oncreate: (vnode) => this.canvas = <HTMLCanvasElement>vnode.dom,
                        onupdate: () => this.drawCanvas(),
                    })
                )
            )
        ]);

    }
    private drawCanvas() {
        if( !this.canvas || !this.element ) {
            // Both canvas and element has to be initialized;
            return;
        }
        // var computedStyle = this.element().getComputedStyle();
        const cs = window.getComputedStyle(this.element);
        const width = parseInt(cs.getPropertyValue('width'), 10);
        const height = parseInt(cs.getPropertyValue('height'), 10);

        this.canvas.width = width;
        this.canvas.height = height;

        const context: CanvasRenderingContext2D = this.canvas.getContext('2d')!; // it should exist

        this.highlighter.getAreas().forEach((area: Area) => {
            if( area.points.length > 0 ) {
                let scaled = this.pointByElementSize(area.points[0].x, area.points[0].y);
                context.beginPath();
                context.moveTo(scaled.x, scaled.y);
                for(let i = 1; i < area.points.length; i++ ) {
                    let scaled = this.pointByElementSize(area.points[i].x, area.points[i].y);
                    context.lineTo(scaled.x, scaled.y);
                }
                context.closePath();
                context.lineWidth = 2;

                let style = area.style;
                const areaCategory = this.highlighter.getCategoryByValue(area.getSelectedValue());
                if( areaCategory ) {
                    style = areaCategory.style;
                }
                if( this.selectedArea && this.selectedArea.equals(area)) {
                    style = area.styleHover;
                }
                context.fillStyle = style.fillColor;
                context.fill();
                context.strokeStyle = style.borderColor;
                context.stroke();
            }
        });
    }

    /**
     * Convert image-space coordinates to screen-space coordinates  (or reverse)
     */
    public pointByElementSize(x: number, y: number, reverse: boolean = false): Point {
        const r = this.ratio;

        if (reverse) {
            return new Point(x/r.w, y/r.h);
        }
        return new Point(x*r.w,y*r.h);
    }

    private evtToPoint(evt: MouseEvent) : Point {
        const rect = this.element!.getBoundingClientRect();

        // Convert to image-space coordinates
        return this.pointByElementSize(
            evt.clientX - rect.left, // x coordinate in image (screen-space)
            evt.clientY - rect.top, // y coordinate in image (screen-space)
            true
        );
    }

    private viewCategories() {
        const categories = this.highlighter.getCategories();
        if( categories.length === 1 ) {
            // Do not show one category - it is selected implicitly
            return null;
        } else {
            // @ts-ignore
            return m("div.categories", categories.map( (cat: Category) => m('span.category', {
                    style: `background-color: ${cat.style.fillColor}`,
                    'class': this.selectedCategory.value === cat.value ? 'selected' : null,
                    onclick: () => this.selectedCategory = cat,
                }, this.highlighter.imageItem.questionary.renderText(cat.name)))
            );
        }
    }

    private onResize() {
        this.computeRatio();
        m.redraw();
    }

    oncreate() {
        $(window).on('resize', this.onResize.bind(this));
    }

    onremove() {
        $(window).off('resize', this.onResize.bind(this))
    }

}